import React from 'react';
import Modal from '@/ctComponents/Modal';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { noScroll, removeNoScroll } from '@/actions/Helper';
import { TableV2 } from '@/components/TableV2';
import { ArrowRight } from 'lucide-react';
import { TerminalsIcon } from '@/assets';
import { useHistory } from 'react-router-dom';
import {
  suspendWarning,
  unsuspendWarning,
  resetWarning,
  SaveApiResMessage,
} from '@/actions';
import { SuspendDatePickerButton } from './SuspendDatePicker';
import CustomCheckbox from '../CustomCheckbox';

const TABLE_HEADERS_NON_ADMIN_BASE = [
  '#',
  'Terminal',
  'Status',
  'Missed',
  'Missed Window',
  'Allowed Limit',
  'Warning Ends',
  'Missed (Recent 7 days)',
  'Action',
];

const TABLE_HEADERS_ADMIN_BASE = [
  '#',
  'Terminal',
  'Status',
  'Days in Status',
  'Missed',
  'Missed Window',
  'Threshold',
  'Warning Ends',
  'Missed (Recent 7 days)',
  'Suspension Days Left',
  'PreSuspended',
  'Action',
];

const insertExtraColumnHeaders = (baseHeaders, extraColumns) => {
  if (!extraColumns?.previousMonth || !extraColumns?.currentMonth)
    return baseHeaders;
  const insertIndex = baseHeaders.length - 1; // before Action
  return [
    ...baseHeaders.slice(0, insertIndex),
    extraColumns.previousMonth.title ?? 'Previous Month',
    extraColumns.currentMonth.title ?? 'Current Month',
    ...baseHeaders.slice(insertIndex),
  ];
};

const getExtraColumnCount = (extraColumns, terminalId, monthKey) => {
  if (!extraColumns?.[monthKey]?.values || terminalId == null) return 0;
  const key = String(terminalId);
  const item = extraColumns[monthKey].values[key];
  return item?.count ?? 0;
};

export const WarningsModal = ({
  show,
  setShow,
  data,
  isAdmin = false,
  onRefetchData,
  warningsExtraColumns = null,
}) => {
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [showResetModal, setShowResetModal] = useState({
    show: false,
    data: null,
  });

  useEffect(() => {
    show ? noScroll() : removeNoScroll();
  }, [show]);

  const handleReviewAppointments = (item) => {
    const terminalName = item.terminal?.slug;
    if (terminalName) {
      const terminalSlug =
        typeof terminalName === 'string'
          ? terminalName
          : terminalName.slug || terminalName.name || '';
      history.push(
        `/my-appointment/?terminalName=${encodeURIComponent(
          terminalSlug
        )}&terminalId=${encodeURIComponent(item.terminal_id)}`
      );
      setShow(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const options = { month: 'short', day: 'numeric' };
    return date.toLocaleDateString('en-US', options);
  };

  const getWarningStatusColor = (type) => {
    switch (type) {
      case 'FINAL':
        return 'bg-light-secondary-yellow border-warning-final-light';
      case 'SUSPENDED':
        return 'bg-error-light border-warning-suspend-light';
      case 'INITIAL':
        return 'bg-secondary-yellow-light border-warning-initial-light';
      default:
        return 'bg-success-light border-warning-green-light';
    }
  };

  const formatDateTimeForAPI = (date) => {
    if (!date) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}`;
  };

  const handleSuspend = async (item, startDate, endDate) => {
    if (loading) return;

    const user_id = item.user_id;
    const warning_uuid = item.uuid;

    const suspend_end = formatDateTimeForAPI(endDate);
    const suspend_start = startDate ? formatDateTimeForAPI(startDate) : null;

    if (!suspend_end) {
      SaveApiResMessage({
        title: 'Error',
        message: 'Please select an end date',
        mode: 'warning',
      });
      return;
    }

    setLoading(true);
    try {
      const response = await suspendWarning({
        user_id,
        warning_uuid,
        suspend_end,
        suspend_start,
      });

      if (response) {
        SaveApiResMessage({
          title: response?.data?.messageHeader || 'Successful',
          message: 'Warning suspended successfully',
        });
        if (onRefetchData) {
          onRefetchData();
        }
      }
    } catch (error) {
      SaveApiResMessage({
        title: 'Error',
        message: error,
        mode: 'warning',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUnsuspend = async (item) => {
    if (loading) return;

    const user_id = item.user_id;
    const warning_uuid = item.uuid;

    setLoading(true);
    try {
      const response = await unsuspendWarning({
        user_id,
        warning_uuid,
      });

      if (response) {
        SaveApiResMessage({
          title: response?.data?.messageHeader || 'Successful',
          message: 'Warning unsuspended successfully',
        });
        if (onRefetchData) {
          onRefetchData();
        }
      }
    } catch (error) {
      SaveApiResMessage({
        title: 'Error',
        message: error,
        mode: 'warning',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResetConfirm = async () => {
    if (loading || !showResetModal.data) return;

    const item = showResetModal.data;
    const user_id = item.user_id;
    const warning_uuid = item.uuid;

    setLoading(true);
    try {
      const response = await resetWarning({
        user_id,
        warning_uuid,
      });

      if (response) {
        setShowResetModal({ show: false, data: null });
        SaveApiResMessage({
          title: response?.data?.messageHeader || 'Successful',
          message: 'Warning reset successfully',
        });
        if (onRefetchData) {
          onRefetchData();
        }
      }
    } catch (error) {
      SaveApiResMessage({
        title: 'Error',
        message: error,
        mode: 'warning',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleReset = (item) => {
    setShowResetModal({ show: true, data: item });
  };

  const renderActionButtons = (item) => {
    const isSuspended =
      item.type === 'suspend' || item.warning_type === 'SUSPENDED';
    const isDisabled = loading || item.warning_type === 'NORMAL';

    return (
      <div className="flex gap-2 justify-center py-2 relative">
        <button
          onClick={() => handleReset(item)}
          disabled={isDisabled}
          className={`px-3 ${
            isDisabled
              ? 'bg-border-light-secondary cursor-default text-light-gray-border'
              : 'hover:shadow-border-button cursor-pointer text-inactive-text bg-white'
          } shadow-none h-6 border hover:outline-solid hover:outline-offset-1 font-bold border-border-tertiary border-solid  text-xs rounded font-averta transition-colors `}>
          Reset
        </button>

        <SuspendDatePickerButton
          item={item}
          isSuspended={isSuspended}
          loading={loading}
          onSuspend={handleSuspend}
          onUnsuspend={handleUnsuspend}
          onClose={() => {}}
        />
      </div>
    );
  };

  const renderTerminalCell = (item) => {
    return (
      <div className="flex mx-auto flex-col max-w-64 items-center gap-1">
        <img
          width={80}
          src={TerminalsIcon[item.terminal?.slug || item.terminal?.name]}
          alt={item.terminal?.slug || item.terminal?.name}
        />
      </div>
    );
  };

  const extraCells = (item) => {
    if (!warningsExtraColumns) return [];
    const tid = item.terminal_id;
    return [
      getExtraColumnCount(warningsExtraColumns, tid, 'previousMonth'),
      getExtraColumnCount(warningsExtraColumns, tid, 'currentMonth'),
    ];
  };

  const injectExtraCells = (cellData, item) => {
    const cells = extraCells(item);
    if (cells.length === 0) return cellData;
    const insertIndex = cellData.length - 1; // before Action
    return [
      ...cellData.slice(0, insertIndex),
      ...cells,
      ...cellData.slice(insertIndex),
    ];
  };

  const prepareTableData = () => {
    if (!data || data.length === 0) return [];

    return data.map((item, index) => {
      if (isAdmin) {
        const cellData = [
          index + 1,
          renderTerminalCell(item),
          <span
            key={`type-${index}`}
            className={`h-5 w-fit min-w-102 px-3 box-border border border-solid rounded-full text-11 flex items-center justify-center mx-auto ${getWarningStatusColor(
              item.warning_type
            )}`}>
            {item.warning_type_text}
          </span>,
          item.warning_days_in || '-',
          item.warning_window_missed || '-',
          <span className="whitespace-nowrap">
            {item.warning_window_text || '-'}
          </span>,
          item.threshold || '-',
          item.suspend_end_text ||
            (item.warning_end_at ? formatDate(item.warning_end_at) : '-'),
          item.current_missed_count || '-',
          item.suspended_remaining_days || '-',
          <span className="flex justify-center items-center">
            <CustomCheckbox checked={item.pre_suspend === true} disabled />
          </span>,
          renderActionButtons(item, index),
        ];
        return {
          id: item.id || index,
          cellData: injectExtraCells(cellData, item),
        };
      }

      // Non-admin view
      const cellData = [
        index + 1,
        renderTerminalCell(item),
        <span
          key={`type-${index}`}
          className={`h-5 w-fit px-3 min-w-102 box-border border border-solid rounded-full text-11 flex items-center justify-center mx-auto ${getWarningStatusColor(
            item.warning_type
          )}`}>
          {item.warning_type_text}
        </span>,
        item.warning_window_missed || '-',
        <span className="whitespace-nowrap">
          {item.warning_window_text || '-'}
        </span>,
        item.threshold || '-',
        item.suspend_end_text ||
          (item.warning_end_at ? formatDate(item.warning_end_at) : '-'),
        item.current_missed_count || '-',
        <span
          onClick={() => handleReviewAppointments(item)}
          className="flex justify-center items-center gap-1 cursor-pointer text-11 text-primary-blue hover:underline">
          {item.warning_type === 'SUSPENDED'
            ? 'View Details'
            : 'Review Appointments'}
          <ArrowRight size={12} />
        </span>,
      ];
      return {
        id: item.id || index,
        cellData: injectExtraCells(cellData, item),
      };
    });
  };

  return (
    <>
      <Modal
        minWidth="unset"
        show={show}
        children={
          <div className="w-1077 overflow-y-scroll overflow-x-hidden lg:overflow-y-hidden">
            <p className="text-primary-text text-sm m-0 my-6">
              {isAdmin ? (
                'Review warning levels and update the member status, including suspension or reset to normal.'
              ) : (
                <>
                  If missed appointments exceed the <b>Allowed Limit</b> , a
                  warning is triggered.
                  <br />
                  Keep missed appointments below the limit for 
                  <b>7 days</b> to clear the warning.
                  <br />
                  If the <b>Final Warning</b> continues for <b>7 days</b>
                  , booking access may be suspended.
                </>
              )}
            </p>
            <TableV2
              minWidth="unset"
              headers={insertExtraColumnHeaders(
                isAdmin
                  ? TABLE_HEADERS_ADMIN_BASE
                  : TABLE_HEADERS_NON_ADMIN_BASE,
                warningsExtraColumns
              )}
              data={prepareTableData()}
              emptyMessage="No warnings available"
            />
          </div>
        }
        icon={'dangerInfo'}
        title={
          isAdmin ? 'Manage Appointment Warnings' : 'Appointment Warning Status'
        }
        setShow={setShow}
      />
      {createPortal(
        <Modal
          minWidth={'unset'}
          titleColor={'#131517'}
          show={showResetModal.show}
          children={
            <div
              style={{
                textAlign: 'center',
                width: '100%',
                marginTop: '24px',
              }}>
              <p className="m-0 font-bold text-primary-text">
                Are you sure you want to reset this terminal?
              </p>
              <p className="m-0 text-secondary-text mt-2">
                This action will clear the current warning status and return the
                terminal to normal.
              </p>
            </div>
          }
          submitText={'Confirm Reset'}
          cancelText="No, close"
          icon={'dangerInfo'}
          title={'Reset Terminal Status'}
          setShow={() => setShowResetModal({ show: false, data: null })}
          submit={handleResetConfirm}
          loadingbutton={loading}
        />,
        document.body
      )}
    </>
  );
};
